import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Federal sales",
  "includeToc": true,
  "layout": "downloads",
  "description": "This dataset contains federal sales data for oil, gas, and natural gas liquids (NGLs) by calendar year. The dataset is for years 2013-2022.",
  "tags": ["Data", "Downloads", "Documentation", "Federal", "Effective royalty rates", "Allowances", "Royalties", "Revenue", "Location", "By location", "Calendar year", "Sales"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const DownloadDataFile = makeShortcode("DownloadDataFile");
const Link = makeShortcode("Link");
const SalesDataDiagram = makeShortcode("SalesDataDiagram");
const FederalSalesTransactionCodesTable = makeShortcode("FederalSalesTransactionCodesTable");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/federal-sales-data/downloads/"
      }}>{`Downloads /`}</a></p>

    <h1 {...{
      "id": "Federal-sales",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales`}</h1>
    <p>{`This dataset contains federal sales data from the Form ONRR-2014 for:`}</p>
    <ul>
      <li parentName="ul">{`oil`}</li>
      <li parentName="ul">{`gas`}</li>
      <li parentName="ul"><GlossaryTerm mdxType="GlossaryTerm">natural gas liquids (NGLs)</GlossaryTerm>  </li>
    </ul>
    <p>{`The data contains reported volumes for royalty and non-royalty bearing transactions by calendar year. This federal sales data represents sales transactions that took place within a given calendar year. The dataset is for calendar years 2013-2022.  `}</p>
 <br />  
    <p>{`Download federal sales data (as of November 6, 2023):`}</p>
    <DownloadDataFile linkType="DownloadCsv" dataSet="federal_sales.csv" mdxType="DownloadDataFile">
  Calendar year federal sales data
    </DownloadDataFile>
    <DownloadDataFile linkType="DownloadXls" dataSet="federal_sales.xlsx" mdxType="DownloadDataFile">
  Calendar year federal sales data, includes data dictionary and notes
    </DownloadDataFile>  
    <Link href='/query-data/?dataType=Federal%20Sales' linkType='FilterTable' mdxType="Link">Filter and query data to customize the dataset</Link>
    <h2 {...{
      "id": "Scope",
      "style": {
        "position": "relative"
      }
    }}>{`Scope`}</h2>
    <p>{`ONRR collects the data from the Sales and Royalty Remittance Report, Form ONRR-2014, which contains information on royalties due based on sales or use of the natural resource production. This dataset is pulled from Form ONRR-2014.  ONRR collects revenues on over 60 different products, but this dataset only includes data for oil, gas, and NGLs. ONRR aggregates the data to develop this federal sales dataset, which is publicly available.`}</p>
    <br />
The dataset includes federal sales data for U.S. federal onshore lands and offshore areas for oil, gas, and NGLs. It does not include Native American land, privately-owned lands or U.S. state lands. The dataset includes all ONRR Form-2014 federal reported royalty transaction codes, royalty relief codes, and transaction code (TC) 13 Quality Bank and Gravity Bank Adjustments.
    <br />
    <h2 {...{
      "id": "Data-publication",
      "style": {
        "position": "relative"
      }
    }}>{`Data publication`}</h2>
    <p>{`The federal sales dataset is updated annually. `}</p>
    <h2 {...{
      "id": "About-the-data",
      "style": {
        "position": "relative"
      }
    }}>{`About the data`}</h2>
    <h3 {...{
      "id": "What-are-the-steps-to-develop-federal-sales-data",
      "style": {
        "position": "relative"
      }
    }}>{`What are the steps to develop federal sales data?`}</h3>
    <p>{`Review the step-by-step diagram to learn about how the federal sales data is developed. Click on the arrows to expand the steps and learn more about the process.`}</p>
    <SalesDataDiagram mdxType="SalesDataDiagram" />
    <h3 {...{
      "id": "What-is-the-difference-between-Sales-Year-and-Accounting-Year-data",
      "style": {
        "position": "relative"
      }
    }}>{`What is the difference between Sales Year and Accounting Year data?`}</h3>
    <p>{`Although they appear similar, there are key differences:  `}</p>
    <br />
    <b>Accounting Year</b> data represent all transactions that ONRR accepted into our financial system during a given fiscal or calendar year. This dataset contains transactions for sales that took place in the current federal fiscal or calendar year, as well as adjusted or corrected transactions for sales that took place in previous federal fiscal or calendar years. Because revenues are generally disbursed in the same year they were reported and accepted into ONRR’s financial system, accounting year data are most useful when analyzing dollars ONRR collected and disbursed in a given federal fiscal or calendar year. Because they include adjusted or corrected transactions for previous sales periods, accounting year data should not be used for trending purposes or for analyzing sales volumes.
    <br /> 
    <br /> 
    <b>Sales Year</b> data represent sales transactions that took place in a given federal fiscal or calendar year and do not include adjusted or corrected transactions for sales that took place in previous years. This dataset provides a “snap shot” of reported sales of mineral commodities, and is most useful when trending sales volumes or when making comparisons to production volumes. Because ONRR processes royalty transactions daily, the sales year data constantly changes. For this reason, ONRR publishes sales year datasets with an as-of date showing when the information was current.
    <h3 {...{
      "id": "What-are-transaction-codes-TC",
      "style": {
        "position": "relative"
      }
    }}>{`What are transaction codes (TC)?`}</h3>
    <p>{`Transaction codes are used transactions on Form ONRR-2014 and organized by function. This dataset does not include all the transaction codes. Other datasets on the site that come from Form ONRR-2014 include all the transaction codes. There are several reasons why some transaction codes are excluded for this data set. The reasons include not specific to royalty collection, being used to only provide estimates, not relating to oil and gas products, not having royalties reported, or relating to minimum payments. `}</p>
    <br />
Below are the transaction codes included in the dataset.
    <br />
    <FederalSalesTransactionCodesTable mdxType="FederalSalesTransactionCodesTable" />
    <h3 {...{
      "id": "How-are-Quality-Bank-and-Gravity-Bank-Adjustments-calculated",
      "style": {
        "position": "relative"
      }
    }}>{`How are Quality Bank and Gravity Bank Adjustments calculated?`}</h3>
    <p>{`Quality Bank and Gravity Bank Adjustments are made prior to any allowances taking place and included in the reporting. These adjustments (TC 13) are used to report the difference in quality (gravity) between oil measured at the BLM or BSEE-approved point of royalty settlement and the oil delivered to the refiner at the delivery point. The dataset rolls up the TC 13 adjustments with the TC 01 (Royalty) reporting for oil. (See `}<a parentName="p" {...{
        "href": "https://www.ecfr.gov/current/title-30/chapter-XII/subchapter-A/part-1206"
      }}>{`30 CFR 1206.119`}</a>{`.)`}</p>
    <h3 {...{
      "id": "What-does-not-tied-to-a-commodity-mean",
      "style": {
        "position": "relative"
      }
    }}>{`What does “not tied to a commodity” mean?`}</h3>
    <p>{`As a part of the COVID royalty relief program reporters were not required to tie their reporting to a specific product code. Due to this ONRR is unable to allocate the reported information to the various products. In this dataset, “not tied to a commodity” references transactions that are identified as COVID royalty relief.`}</p>
    <h3 {...{
      "id": "What-is-COVID-royalty-relief",
      "style": {
        "position": "relative"
      }
    }}>{`What is COVID royalty relief?`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.ecfr.gov/current/title-30/chapter-II/subchapter-A/part-203"
      }}>{`30 CFR 203.80`}</a>{` allows the Bureau of Safety and Environmental Enforcement (BSEE) the ability to grant special case royalty relief on a product or lease basis through an application-based process that adheres to strict criteria. BSEE granted limited royalty relief in 2020 and 2021 that is categorized as COVID royalty relief in our data set. COVID Royalty Relief is given its own category in this dataset as payors were not required to report a product.`}</p>
    <h2 {...{
      "id": "Data-dictionary",
      "style": {
        "position": "relative"
      }
    }}>{`Data dictionary`}</h2>
    <h3 {...{
      "id": "Fields-and-definitions",
      "style": {
        "position": "relative"
      }
    }}>{`Fields and definitions`}</h3>
    <p><strong parentName="p">{`Calendar Year:`}</strong>{` The year in which the data is reported for the period between January 1 and December 31 for a given year. This dataset is sales year data reported on a calendar year basis. `}</p>
    <p>{`Note: Sales year and accounting year are different from calendar year and fiscal year. Refer to above definitions of sales year and accounting year. `}</p>
    <br />
    <p><strong parentName="p">{`Land Class:`}</strong>{` This field distinguishes federal lands and waters from Native American lands. All data in this dataset is federal.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Federal:`}</strong>{`  `}<a parentName="li" {...{
          "href": "https://fas.org/sgp/crs/misc/R42346.pdf"
        }}>{`Federal lands`}</a>{` are owned by or under the jurisdiction of the federal government. Federal lands include: public domain lands, acquired lands, military acquired lands, and the `}<a parentName="li" {...{
          "href": "https://www.boem.gov/OCS-Lands-Act-History/"
        }}>{`Outer Continental Shelf`}</a>{`.`}</li>
    </ul>
    <p><strong parentName="p">{`Land Category:`}</strong></p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Onshore (Federal):`}</strong>{` Lands that are owned by or under the jurisdiction of the federal government and are situated or occurring on land. Onshore land includes public domain lands, acquired lands and military acquired lands.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Offshore (Federal):`}</strong>{` Submerged lands that are located farther than three miles off a state's coastline, or three marine leagues into the Gulf of Mexico off Texas and Western Florida and are owned by or under the jurisdiction of the federal government. `}<a parentName="p" {...{
            "href": "http://www.boem.gov/OCS-Lands-Act-History/"
          }}>{`Outer Continental Shelf`}</a>{` lands are included.`}</p>
      </li>
    </ul>
    <p><strong parentName="p">{`State/Offshore Region:`}</strong>{` The Bureau of Ocean Energy Management (BOEM) separates offshore areas into four regions: Gulf of Mexico, Atlantic, Pacific, and Alaska. For more information on offshore regions, including spatial boundaries, see the Bureau of Ocean Energy Management's `}<a parentName="p" {...{
        "href": "http://www.boem.gov/Maps-and-GIS-Data/"
      }}>{`maps and GIS data`}</a>{`. The state name is also listed under this column.`}</p>
    <br />
    <p><strong parentName="p">{`Revenue Type:`}</strong>{` Revenues from U.S. natural resources fall into one of several types. This dataset contains only royalties and COVID royalty relief as revenue types.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Royalties:`}</strong>{` A natural resource lease holder pays royalties after the lease starts producing a commodity in paying quantities. The amount is based on a percentage of the revenue from the commodity sold. The exact percentage is set in the original lease document that went along with the lease sale.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Covid Royalty Relief:`}</strong>{` This amount is always negative. COVID royalty relief is given its own category in the data as payors were not required to report a product.`}</p>
      </li>
    </ul>
    <p><strong parentName="p">{`Commodity:`}</strong>{` The Department of the Interior collects revenues on over 60 different products, but there are only oil, gas, NGLs, and "not tied to a commodity" in this dataset. “Not tied to a commodity” references COVID royalty relief data. `}</p>
    <br />
    <p><strong parentName="p">{`Sales Volume:`}</strong>{` The volume of the product sold or removed from the lease or agreement and is attributable to the lease or to the lease-agreement combination, sales type code, product code, and sales month reported. The unit of measurement is located under the commodity column. The sales volume (or quantity, if geothermal) reported corresponds to the portion of the total production from the lease or from the lease-agreement combination. This is an industry reported value on the Form ONRR-2014. `}</p>
    <br />
    <p><strong parentName="p">{`Gas MMBtu Volume:`}</strong>{` Volume measured in Million British Thermal Units (MMBtu). One British Thermal Unit (Btu) is the amount of heat required to raise the temperature of one pound of water by one degree Fahrenheit. MMBtu is the globally used energy value measurement. `}</p>
    <br />
    <p><strong parentName="p">{`Sales Value:`}</strong>{` The value of a commodity when sales volume is multiplied by price.`}</p>
    <br />
    <p><strong parentName="p">{`Royalty Value Prior to Allowances (RVPA):`}</strong>{` Calculated by multiplying sales value by royalty rate.`}</p>
    <br />
    <p><strong parentName="p">{`Transportation Allowances (TA):`}</strong>{` Deductions in determining royalty value for the reasonable, actual costs incurred by the lessee, or a deduction accepted or established by ONRR for moving coal from the mine or lease to a remote point of sale or delivery, or moving oil or gas, gas, or geothermal byproducts to a point of sale or delivery off of the lease, unit area, or communitized area.`}</p>
    <br />
    <p><strong parentName="p">{`Processing Allowances (PA):`}</strong>{` Deductions from royalty value for the lessee's actual, reasonable costs of processing a gas stream.`}</p>
    <br />
    <p><strong parentName="p">{`Royalty Value Less Allowances (RVLA):`}</strong>{` Calculated by subtracting allowances (transportation, processing) from royalty value.`}</p>
    <br />
    <p><strong parentName="p">{`Effective Royalty Rate:`}</strong>{` The Office of Inspector General (OIG) created the term and concept of effective royalty rate. The OIG defines the calculation of the effective royalty rate in `}<a href="https://www.doioig.gov/reports/inspection-evaluation/us-department-interior-does-not-analyze-effective-royalty-rates">{` Report 2021-CR-042 June 2023 `}</a>{`.`}</p>
    <br />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      